import { useState } from "react";
import { Play, Youtube } from "lucide-react";
import FadeInSection from "../../ui/fade-in-section";
import SectionHeader from "../../ui/section-header";
import { extractYoutubeId } from "../../../utils/sessions-sheet";

function LearningTags({ learnings }) {
  if (!learnings?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {learnings.map((item) => (
        <span
          key={item}
          className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-50 text-slate-600 border border-slate-100"
        >
          {item}
        </span>
      ))}
    </div>
  );
}

function SessionVideoCard({ session, isPlaying, onPlay }) {
  const videoId = session.videoId || extractYoutubeId(session.recordingLink);
  const [thumbSrc, setThumbSrc] = useState(
    session.thumbnail ||
      (videoId ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` : "")
  );

  return (
    <article className="home-card rounded-2xl overflow-hidden h-full flex flex-col group">
      <div className="relative aspect-video bg-slate-900 overflow-hidden">
        {videoId && isPlaying ? (
          <iframe
            className="absolute inset-0 w-full h-full border-0"
            src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`}
            title={session.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        ) : videoId ? (
          <button
            type="button"
            onClick={onPlay}
            className="absolute inset-0 w-full h-full text-left"
            aria-label={`Play ${session.title}`}
          >
            <img
              src={thumbSrc}
              alt=""
              className="w-full h-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-[1.03]"
              onError={() => {
                if (videoId && !thumbSrc.includes("mqdefault")) {
                  setThumbSrc(`https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`);
                }
              }}
            />
            <span className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-950/10 to-transparent" />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/95 text-[#991B1B] shadow-lg shadow-black/25 transition-transform duration-200 group-hover:scale-110">
                <Play className="w-6 h-6 fill-current ml-0.5" aria-hidden />
              </span>
            </span>
          </button>
        ) : session.recordingLink ? (
          <a
            href={session.recordingLink}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-slate-900 to-slate-800"
            aria-label={`Watch ${session.title} on YouTube`}
          >
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10 text-white">
              <Youtube className="w-7 h-7" aria-hidden />
            </span>
            <span className="text-white/80 text-sm font-medium">
              Watch on YouTube
            </span>
          </a>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-100 text-slate-400 text-sm">
            Recording coming soon
          </div>
        )}
      </div>

      <div className="p-4 sm:p-5 flex flex-col flex-grow">
        <div className="flex items-start gap-2.5 mb-3">
          <span className="inline-flex items-center justify-center min-w-[2rem] h-8 px-1.5 rounded-lg bg-[#14B48C]/10 text-[#14B48C] font-bold text-xs tabular-nums shrink-0">
            {session.number}
          </span>
          <h3 className="font-semibold text-slate-900 text-sm sm:text-[15px] leading-snug">
            {session.title}
          </h3>
        </div>
        <LearningTags learnings={session.learnings} />
      </div>
    </article>
  );
}

export default function SessionsPast({ sessions, loading }) {
  const [playingId, setPlayingId] = useState(null);

  return (
    <section className="saas-section bg-white overflow-hidden relative">
      <div className="page-container">
        <FadeInSection>
          <SectionHeader
            eyebrow="ARCHIVE"
            title="Past Sessions"
            subtitle="Missed a session? Every recording is available below."
          />
        </FadeInSection>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="rounded-2xl overflow-hidden bg-slate-100"
              >
                <div className="aspect-video animate-pulse bg-slate-200" />
                <div className="h-28" />
              </div>
            ))}
          </div>
        ) : !sessions.length ? (
          <p className="text-center text-slate-500 text-base sm:text-lg py-10">
            No past sessions yet — check back after our next live session.
          </p>
        ) : (
          <FadeInSection
            stagger={0.08}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6"
          >
            {sessions.map((session) => (
              <SessionVideoCard
                key={session.number}
                session={session}
                isPlaying={playingId === session.number}
                onPlay={() => setPlayingId(session.number)}
              />
            ))}
          </FadeInSection>
        )}
      </div>
    </section>
  );
}
